/**
 * @name: Custom Jotform section visibility
 * @description: Toggles page sections by class for form vs success states.
 * @strict-mode: true
 */

(() => {
  const HIDDEN_CLASS = 'amb-jotform-section-hidden';

  /**
   * @param {string} value
   * @returns {string[]}
   */
  const parseClasses = (value) => {
    if (!value) return [];

    return value
      .split(/[\n,]+/)
      .map((item) => item.trim().replace(/^\./, ''))
      .filter(Boolean);
  };

  /**
   * @param {string[]} classNames
   * @returns {HTMLElement[]}
   */
  const getElementsByClassList = (classNames) => {
    const elements = [];

    for (const className of classNames) {
      const matches = document.getElementsByClassName(className);

      for (const el of matches) {
        if (el instanceof HTMLElement) {
          elements.push(el);
        }
      }
    }

    return elements;
  };

  /**
   * @param {HTMLElement[]} elements
   * @param {boolean} visible
   */
  const setVisibility = (elements, visible) => {
    for (const el of elements) {
      el.classList.toggle(HIDDEN_CLASS, !visible);
      el.hidden = !visible;
    }
  };

  /**
   * @param {HTMLElement} root
   * @param {'form' | 'success'} state
   */
  const applyState = (root, state) => {
    const withForm = getElementsByClassList(parseClasses(root.dataset.showWithForm || ''));
    const onSuccess = getElementsByClassList(parseClasses(root.dataset.showOnSuccess || ''));

    if (state === 'success') {
      setVisibility(withForm, false);
      setVisibility([root], false);
      setVisibility(onSuccess, true);

      const firstSuccess = onSuccess[0];
      if (firstSuccess) {
        firstSuccess.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      return;
    }

    setVisibility([root], true);
    setVisibility(withForm, true);
    setVisibility(onSuccess, false);
  };

  const init = () => {
    const roots = document.querySelectorAll('[data-amb-jotform-section]');

    for (const root of roots) {
      if (!(root instanceof HTMLElement)) continue;
      if (root.dataset.ambJotformSectionsInit === '1') continue;

      root.dataset.ambJotformSectionsInit = '1';
      applyState(root, 'form');
    }
  };

  document.addEventListener('amb:jotform:success', (event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    const source = detail?.source;

    if (source instanceof HTMLElement) {
      const root = source.closest('[data-amb-jotform-section]');
      if (root instanceof HTMLElement) {
        applyState(root, 'success');
        return;
      }
    }

    const roots = document.querySelectorAll('[data-amb-jotform-section]');
    for (const root of roots) {
      if (root instanceof HTMLElement) {
        applyState(root, 'success');
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
